import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  TextField,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Box,
  Typography,
  Grid,
  Card,
  CardContent
} from '@mui/material';
import StepWizard from '../../components/common/StepWizard';
import { useAppContext } from '../../context/AppContext';
import { mockThemeOptions, mockLayoutOptions } from '../../data/mockData';

const Step1BasicInfo = ({ data, onDataChange }) => (
  <Box>
    <Typography variant="h6" gutterBottom>
      Basic Information
    </Typography>
    <TextField
      fullWidth
      label="Store Name"
      value={data.name || ''}
      onChange={(e) => onDataChange({ name: e.target.value })}
      margin="normal"
      required
    />
    <TextField
      fullWidth
      label="Store Description"
      value={data.description || ''}
      onChange={(e) => onDataChange({ description: e.target.value })}
      margin="normal"
      multiline
      rows={4}
    />
  </Box>
);

const Step2Theme = ({ data, onDataChange }) => (
  <Box>
    <Typography variant="h6" gutterBottom>
      Select Theme
    </Typography>
    <FormControl component="fieldset" sx={{ mt: 2 }}>
      <RadioGroup
        value={data.theme || ''}
        onChange={(e) => onDataChange({ theme: e.target.value })}
      >
        <Grid container spacing={2}>
          {mockThemeOptions.map((theme) => (
            <Grid item xs={12} sm={6} key={theme.id}>
              <Card sx={{ border: data.theme === theme.id ? '2px solid #1976d2' : 'none' }}>
                <CardContent>
                  <FormControlLabel
                    value={theme.id}
                    control={<Radio />}
                    label={
                      <Box>
                        <Typography variant="subtitle1">{theme.name}</Typography>
                        <Box
                          sx={{
                            width: 60,
                            height: 30,
                            backgroundColor: theme.preview,
                            borderRadius: 1,
                            mt: 1
                          }}
                        />
                      </Box>
                    }
                  />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </RadioGroup>
    </FormControl>

    <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
      Select Layout
    </Typography>
    <FormControl component="fieldset" sx={{ mt: 2 }}>
      <RadioGroup
        value={data.layout || ''}
        onChange={(e) => onDataChange({ layout: e.target.value })}
      >
        {mockLayoutOptions.map((layout) => (
          <FormControlLabel
            key={layout.id}
            value={layout.id}
            control={<Radio />}
            label={
              <Box>
                <Typography variant="subtitle1">{layout.name}</Typography>
                <Typography variant="body2" color="textSecondary">
                  {layout.description}
                </Typography>
              </Box>
            }
            sx={{ mb: 2 }}
          />
        ))}
      </RadioGroup>
    </FormControl>
  </Box>
);

const Step3DomainCDN = ({ data, onDataChange }) => (
  <Box>
    <Typography variant="h6" gutterBottom>
      Domain & CDN Configuration
    </Typography>
    <TextField
      fullWidth
      label="Domain"
      value={data.domain || ''}
      onChange={(e) => onDataChange({ domain: e.target.value })}
      margin="normal"
      placeholder="e.g., shop.example.com"
      required
    />
    <TextField
      fullWidth
      label="CDN Provider"
      value={data.cdnProvider || ''}
      onChange={(e) => onDataChange({ cdnProvider: e.target.value })}
      margin="normal"
      placeholder="e.g., Cloudflare, AWS CloudFront"
    />
    <TextField
      fullWidth
      label="CDN Configuration"
      value={data.cdnConfig || ''}
      onChange={(e) => onDataChange({ cdnConfig: e.target.value })}
      margin="normal"
      multiline
      rows={3}
      placeholder="CDN configuration details"
    />
  </Box>
);

const Step4CMS = ({ data, onDataChange }) => (
  <Box>
    <Typography variant="h6" gutterBottom>
      Content Management (CMS)
    </Typography>
    <TextField
      fullWidth
      label="Homepage Title"
      value={data.homeTitle || ''}
      onChange={(e) => onDataChange({ homeTitle: e.target.value })}
      margin="normal"
    />
    <TextField
      fullWidth
      label="Homepage Subtitle"
      value={data.homeSubtitle || ''}
      onChange={(e) => onDataChange({ homeSubtitle: e.target.value })}
      margin="normal"
    />
    <TextField
      fullWidth
      label="Homepage Content"
      value={data.homeContent || ''}
      onChange={(e) => onDataChange({ homeContent: e.target.value })}
      margin="normal"
      multiline
      rows={6}
      placeholder="Enter the main content for the homepage..."
    />
  </Box>
);

const Step5Review = ({ data }) => (
  <Box>
    <Typography variant="h6" gutterBottom>
      Review Configuration
    </Typography>
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle2" color="textSecondary">Store Name</Typography>
      <Typography variant="body1" gutterBottom>{data.name || 'Not set'}</Typography>

      <Typography variant="subtitle2" color="textSecondary" sx={{ mt: 2 }}>Store Description</Typography>
      <Typography variant="body1" gutterBottom>{data.description || 'Not set'}</Typography>

      <Typography variant="subtitle2" color="textSecondary" sx={{ mt: 2 }}>Theme</Typography>
      <Typography variant="body1" gutterBottom>
        {mockThemeOptions.find(t => t.id === data.theme)?.name || 'Not set'}
      </Typography>

      <Typography variant="subtitle2" color="textSecondary" sx={{ mt: 2 }}>Layout</Typography>
      <Typography variant="body1" gutterBottom>
        {mockLayoutOptions.find(l => l.id === data.layout)?.name || 'Not set'}
      </Typography>

      <Typography variant="subtitle2" color="textSecondary" sx={{ mt: 2 }}>Domain</Typography>
      <Typography variant="body1" gutterBottom>{data.domain || 'Not set'}</Typography>

      <Typography variant="subtitle2" color="textSecondary" sx={{ mt: 2 }}>CDN Provider</Typography>
      <Typography variant="body1" gutterBottom>{data.cdnProvider || 'Not set'}</Typography>
    </Box>
  </Box>
);

const EcommerceWizard = () => {
  const { addSalesChannel } = useAppContext();
  const navigate = useNavigate();

  const steps = [
    { label: 'Basic Information', component: <Step1BasicInfo /> },
    { label: 'Theme & Layout', component: <Step2Theme /> },
    { label: 'Domain & CDN', component: <Step3DomainCDN /> },
    { label: 'Content Management', component: <Step4CMS /> },
    { label: 'Review & Create', component: <Step5Review /> }
  ];

  const handleComplete = (formData) => {
    const channel = {
      name: formData.name,
      type: 'ecommerce',
      typeName: 'E-commerce',
      domain: formData.domain,
      config: formData
    };
    addSalesChannel(channel);
    navigate('/sales-channels');
  };

  return <StepWizard steps={steps} onComplete={handleComplete} title="Create E-commerce Channel" />;
};

export default EcommerceWizard;
